import { useCallback, useRef } from "react"
import { useSearchParams } from "react-router-dom"
import { copyUrlSearchParams } from "@ciq/shared/utils/object"

/**
 * Constants for search-related URL query parameters.
 * Used to maintain search state in the URL for browser navigation and bookmarking.
 */
export const SearchQueryArgs = {
  SEARCH: {
    IS_SEARCHING: "isSearching",
    IS_PROCESSING: "isSearchProcessing",
    VALUE: "search",
  },
}

/**
 * Array of all search-related query parameter keys.
 * Use this when clearing or manipulating search params to ensure consistency.
 */
export const SEARCH_QUERY_KEYS = [
  SearchQueryArgs.SEARCH.IS_SEARCHING,
  SearchQueryArgs.SEARCH.IS_PROCESSING,
  SearchQueryArgs.SEARCH.VALUE,
]

/**
 * @typedef {Object} SearchQueryParamsResult
 * @property {boolean} isSearching - Boolean indicating if a search is currently active
 * @property {boolean} isProcessing - Boolean indicating if a search is currently being processed
 * @property {string|null} search - The current search value, null if no search active
 * @property {string|null} loweredSearch - The current search value in lowercase, null if no search active
 * @property {(value: string|null) => void} onSearch - Function to initiate a search or clear search state
 */

/**
 * Custom hook for managing search query parameters in the URL.
 * Provides search state and actions while maintaining URL synchronization for browser navigation.
 * Automatically handles search state persistence and cleanup.
 *
 * @returns {SearchQueryParamsResult} Hook result object with search state and actions
 */
export const useSearchQueryParams = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  // use a ref for the setter method so that we don't get unnecessary re-renders
  const setSearchParamsRef = useRef(setSearchParams)
  if (setSearchParamsRef.current !== setSearchParams) {
    setSearchParamsRef.current = setSearchParams
  }

  const isSearching = searchParams.get(SearchQueryArgs.SEARCH.IS_SEARCHING) === "true"
  const isSearchProcessing = searchParams.get(SearchQueryArgs.SEARCH.IS_PROCESSING) === "true"
  const search = searchParams.get(SearchQueryArgs.SEARCH.VALUE)
  const loweredSearch =
    searchParams.get(SearchQueryArgs.SEARCH.VALUE) != null
      ? searchParams.get(SearchQueryArgs.SEARCH.VALUE).toLowerCase()
      : null

  const onSearch = useCallback(value => {
    setSearchParamsRef.current(old => {
      const data = copyUrlSearchParams(old, {
        keysToSkip: SEARCH_QUERY_KEYS,
      })

      if (value != null) {
        data[SearchQueryArgs.SEARCH.IS_SEARCHING] = true
        data[SearchQueryArgs.SEARCH.VALUE] = value
      }

      return data
    })
  }, [])

  return {
    isSearching: isSearching,
    isProcessing: isSearchProcessing,
    search,
    loweredSearch: loweredSearch,
    onSearch,
  }
}
